import React, { useState, useRef } from 'react';
import { storage } from '../../firebase/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../utils/imageUtils';

const ProductImageUpload = ({ images = [], onChange, maxImages = 5 }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const remainingSlots = maxImages - images.length;
  const canUploadMore = remainingSlots > 0;

  const handleFileSelect = async (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`ไฟล์ ${file.name} ไม่ใช่ไฟล์รูปภาพ`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert(`ไฟล์ ${file.name} มีขนาดใหญ่เกินไป (ไม่เกิน 5MB)`);
        return false;
      }
      return true;
    });

    const filesToAdd = validFiles.slice(0, remainingSlots);
    
    if (filesToAdd.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadPromises = filesToAdd.map(async (file, index) => {
        setUploadProgress((index / filesToAdd.length) * 50);
        
        let fileToUpload = file;
        
        // บีบอัดรูปภาพ (ถ้ามี compressImage function)
        try {
          if (compressImage && typeof compressImage === 'function') {
            fileToUpload = await compressImage(file, 1200, 1200, 0.85);
          }
        } catch (compressError) {
          console.warn('Error compressing image, using original:', compressError);
          // ใช้ไฟล์เดิมถ้าบีบอัดไม่ได้
          fileToUpload = file;
        }
        
        setUploadProgress(50 + (index / filesToAdd.length) * 50);
        
        // สร้างชื่อไฟล์ที่ไม่ซ้ำ
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 8);
        const fileExtension = file.name.split('.').pop();
        const fileName = `product_${timestamp}_${index}_${randomString}.${fileExtension}`;
        const storagePath = `products/${fileName}`;
        
        // อัพโหลดไฟล์
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, fileToUpload);
        const downloadURL = await getDownloadURL(storageRef);
        
        return {
          url: downloadURL,
          name: file.name,
          size: file.size
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      onChange([...images, ...uploadedImages]);
      
      setUploadProgress(100);
      setUploading(false);
    } catch (error) {
      console.error('Error uploading images:', error);
      let errorMessage = 'เกิดข้อผิดพลาดในการอัพโหลด';
      
      if (error.code === 'storage/unauthorized') {
        errorMessage = 'ไม่มีสิทธิ์อัพโหลดไฟล์ กรุณาติดต่อผู้ดูแลระบบ';
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage = 'พื้นที่เก็บข้อมูลเต็ม กรุณาติดต่อผู้ดูแลระบบ';
      } else if (error.code === 'storage/canceled') {
        errorMessage = 'การอัพโหลดถูกยกเลิก';
      } else {
        errorMessage = `เกิดข้อผิดพลาด: ${error.message || error.code || 'ไม่ทราบสาเหตุ'}`;
      }
      
      alert(errorMessage);
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files);
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
  };

  return (
    <div className="space-y-4">
      <label className="block text-gray-700 font-semibold mb-2">
        รูปภาพสินค้า ({images.length}/{maxImages})
      </label>

      {/* พื้นที่ลากวาง */}
      {canUploadMore && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-300 hover:border-gray-400'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <div className="text-4xl mb-2">📸</div>
          <p className="text-lg font-medium text-gray-700 mb-2">
            {uploading ? 'กำลังอัพโหลด...' : 'ลากรูปมาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์'}
          </p>
          <p className="text-sm text-gray-500">
            รองรับไฟล์: JPG, PNG, GIF (ขนาดไม่เกิน 5MB) - สามารถอัพโหลดได้ {remainingSlots} รูป
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
            disabled={uploading || !canUploadMore}
          />
        </div>
      )}

      {/* Progress Bar */}
      {uploading && (
        <div>
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>กำลังอัพโหลด...</span>
            <span>{Math.round(uploadProgress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* รูปภาพที่อัพโหลดแล้ว */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-gray-200">
                <img
                  src={image.url}
                  alt={`Product ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <button
                onClick={() => removeImage(index)}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                title="ลบรูปภาพ"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {index === 0 && (
                <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                  รูปหลัก
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!canUploadMore && images.length > 0 && (
        <p className="text-sm text-gray-500">
          คุณอัพโหลดรูปครบจำนวนสูงสุดแล้ว ({maxImages} รูป)
        </p>
      )}
    </div>
  );
};

export default ProductImageUpload;


